import React, { useState, useEffect, useCallback, useRef } from 'react';
// Icons used by the settings section registry (rail labels) only — section
// bodies import their own icons.
import { FileText, Building, Users, CreditCard, Bot, BookCopy, Calendar, ListTree, Receipt, Upload, Wallet, Inbox as InboxIcon, GraduationCap, Zap, Landmark, Package, Store, Settings as SettingsIcon } from "lucide-react";
import { useParams, useLocation } from 'react-router-dom';
import PageHeader from "../../layouts/PageHeader";
import { getApiUrl } from '../../../utils/ApiUtils';
import axios from '../../../services/AxiosInstance';
import {
  listBookkeepingTemplates,
  submitTemplateForSharing,
  deleteBookkeepingTemplate,
} from '../../../services/BookkeepingTemplateService';
import swal from "sweetalert";
import { useHistory } from 'react-router-dom';
import { useInvoiceSettings } from '../../../context/InvoiceSettingsContext';
import { useQuoteSettings } from '../../../context/QuoteSettingsContext';
import { useFeatures } from '../../../context/FeatureContext';
import { useCapabilities } from '../../../context/CapabilityContext';

import SettingsRail from './SettingsRail';
import useHashScroll from './useHashScroll';
import CompanyDetailsSection from './sections/CompanyDetailsSection';
import FiscalYearsSection from './sections/FiscalYearsSection';
import ChartSection from './sections/ChartSection';
import TemplatesSection from './sections/TemplatesSection';
import VatSection from './sections/VatSection';
import ImportExportSection from './sections/ImportExportSection';
import PayrollSection from './sections/PayrollSection';
import AutomationSection from './sections/AutomationSection';
import UsersSection from './sections/UsersSection';
import InvoiceSection from './sections/InvoiceSection';
import QuotesSection from './sections/QuotesSection';
import SubscriptionSection from './sections/SubscriptionSection';
import AgentsSection from './sections/AgentsSection';
import CustomAgentsSection from './sections/CustomAgentsSection';
import AgentSkillsSection from './sections/AgentSkillsSection';
import MeetTheTeamSection from './sections/MeetTheTeamSection';
import EmailInboxSection from './sections/EmailInboxSection';
import ZettleSection from './sections/ZettleSection';
import AssetsSection from './sections/AssetsSection';
import BankConnectionSettings from './BankConnectionSettings';
import RoleModal from './UserRoleModal';
import OwnershipSection from './OwnershipSection';
import InviteUserModal from './InviteUserModal';
import LinkEmployeeModal from './LinkEmployeeModal';
import SieVerificationModal from '../Modals/SieVerificationModal';
import SieExportModal from '../Modals/SieExportModal';

import CreateFiscalYearModal from '../Accounting/CreateFiscalYearModal';
import PlanUpgradeModal from './Subscription/PlanUpgradeModal';

import { toast } from 'sonner';

// Single source of truth for the settings sections — drives both the
// left rail (grouping, icons, labels) and URL routing (slug). Slugs match
// the historical tab eventKeys so deep links and lazy-load keys are stable.
// `feature` gates a section behind hasFeature(...); ungated sections always show.
const SETTINGS_SECTIONS = [
  // `keywords` are extra search terms for the rail — the words a user actually
  // types for something that lives in this section but is not in its label.
  { slug: 'company', label: 'Företagsuppgifter', Icon: Building, group: 'Företag',
    keywords: ['organisationsnummer', 'adress', 'kontakt', 'säte', 'företagsform', 'k2', 'k3', 'f-skatt', 'sni'] },
  { slug: 'users', label: 'Användare & roller', Icon: Users, group: 'Företag',
    keywords: ['behörighet', 'inbjudan', 'bjud in', 'anställd', 'rättigheter', 'roller'] },
  { slug: 'subscription', label: 'Prenumeration & krediter', Icon: CreditCard, group: 'Företag',
    keywords: ['plan', 'betalning', 'krediter', 'tillägg', 'addon', 'uppsägning', 'stripe', 'pris', 'faktura'] },

  // Bokföring was one route holding eight unrelated cards — 25 124px, 36
  // screens, 29 621 DOM nodes, eight times the next-worst page. These five are
  // that page, split along the lines people actually go looking for.
  { slug: 'fiscal-years', label: 'Räkenskapsår', Icon: Calendar, group: 'Bokföring',
    keywords: ['räkenskapsår', 'bokföringsår', 'öppna år', 'stäng år', 'period'] },
  { slug: 'chart', label: 'Kontoplan', Icon: ListTree, group: 'Bokföring',
    keywords: ['konton', 'bas', 'kontoplanshälsa', 'kontoplansroller', 'roller', 'hälsa', 'konto'] },
  { slug: 'templates', label: 'Bokföringsmallar', Icon: BookCopy, group: 'Bokföring',
    keywords: ['mallar', 'mall', 'verifikationsmall', 'återkommande'] },
  { slug: 'vat', label: 'Moms', Icon: Receipt, group: 'Bokföring',
    keywords: ['moms', 'momsstatus', 'momsnummer', 'momsprofil', 'vat', 'avdragsprofil', 'blandad verksamhet', 'periodisk sammanställning', 'skatteverket'] },
  { slug: 'assets', label: 'Tillgångar', Icon: Package, group: 'Bokföring',
    keywords: ['avskrivning', 'inventarier', 'anläggningstillgångar', 'nyttjandeperiod', 'anläggningsregister'] },
  { slug: 'import-export', label: 'Import & export', Icon: Upload, group: 'Bokföring',
    keywords: ['sie', 'import', 'export', 'flytta bokföring', 'csv', 'excel'] },

  { slug: 'invoice', label: 'Fakturor', Icon: FileText, group: 'Försäljning',
    keywords: ['fakturamall', 'betalvillkor', 'påminnelse', 'dröjsmålsränta', 'rot', 'rut', 'numrering', 'leveransmetoder', 'betalningsmetoder'] },
  // Offerter hidden with the quotes module (2026-09-09); see jsx/index.js.
  // { slug: 'quotes', label: 'Offerter', Icon: ClipboardList, group: 'Försäljning', feature: 'invoicing_quotes',
  //   keywords: ['offertmall', 'giltighetstid'] },
  { slug: 'payroll', label: 'Lön & semester', Icon: Wallet, group: 'Personal',
    keywords: ['lön', 'semester', 'semesterår', 'agi', 'arbetsgivaravgift', 'personal', 'anställd'] },

  // UX redesign Phase D: everything automation-config in ONE section —
  // Regler (regelverk), Automatisk bokföring (earned autonomy + Nödstopp),
  // AI-historik, Dela anonym statistik.
  { slug: 'automation', label: 'Automatisering', Icon: Zap, group: 'Automation & AI', feature: 'ai_reconciliation',
    keywords: ['regler', 'matchning', 'nödstopp', 'ai-historik', 'statistik', 'autonomi'] },
  { slug: 'agents', label: 'AI-agenter', Icon: Bot, group: 'Automation & AI', feature: 'ai_invoice_agent',
    keywords: ['godkännande', 'attest', 'leverantörsfaktura', 'automatik', 'slack', 'teams'] },
  { slug: 'custom-agents', label: 'Egna agenter', Icon: Bot, group: 'Automation & AI', feature: 'custom_agents',
    keywords: ['bygg agent', 'anpassad', 'prompt'] },
  { slug: 'agent-skills', label: 'Agentrutiner', Icon: GraduationCap, group: 'Automation & AI', feature: 'agent_skills',
    keywords: ['skills', 'rutiner', 'kunskap', 'instruktioner'] },
  { slug: 'team', label: 'Möt teamet', Icon: Users, group: 'Automation & AI', feature: 'ai_assistant',
    keywords: ['agenter', 'finn', 'mats', 'personas', 'ai'] },

  { slug: 'bank-connections', label: 'Bankanslutningar', Icon: Landmark, group: 'Integrationer', feature: 'bank_feed',
    keywords: ['bank', 'enable banking', 'swedbank', 'transaktioner', 'bankkonto', 'psd2'] },
  { slug: 'email-inbox', label: 'E-postinkorg', Icon: InboxIcon, group: 'Integrationer',
    keywords: ['vidarebefordran', 'forwarding', 'gmail', 'outlook', 'blocklista', 'inkorg', 'e-post'] },
  // ONE flag covers every payment/sales-channel connector (Zettle today,
  // Shopify/Klarna/Stripe later) — plan decision D3.
  { slug: 'zettle', label: 'Zettle', Icon: Store, group: 'Integrationer', feature: 'zettle_integration',
    keywords: ['kortbetalning', 'kassa', 'pos', 'izettle', 'försäljning'] },
];
const DEFAULT_SECTION = 'company';

// Groups that start collapsed. Företag and Bokföring are where the daily work
// is; these three are configure-once surfaces, and leaving all five open makes
// a 19-leaf rail taller than the viewport. The group holding the active
// section always opens regardless, and rail search ignores collapse entirely.
const DEFAULT_COLLAPSED_GROUPS = ['Försäljning', 'Personal', 'Automation & AI', 'Integrationer'];

// Slugs that used to exist, and where they went. Bookmarks, the links in
// YearEnd/Verifikat/rapporterna, and anything a user has in their own notes all
// still resolve — `bookkeeping` alone was linked from nine places in the app.
// The hash carries the old within-page anchor, which is what actually says
// which of the five new pages the caller wanted.
const LEGACY_SECTION_REDIRECTS = {
  bookkeeping: {
    default: 'fiscal-years',
    byHash: {
      rakenskapsar: 'fiscal-years',
      'import-export': 'import-export',
      mallar: 'templates',
      kontoplan: 'chart',
      kontoplansroller: 'chart?vy=roller',
      kontoplanshalsa: 'chart?vy=halsa',
      lon: 'payroll',
      moms: 'vat',
    },
  },
};

const CompanySettings = () => {
  const { companyId, section } = useParams();
  const { hash } = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const history = useHistory();

  // Only the loading flags are needed in the shell now — the invoice/quote
  // settings themselves are consumed directly by InvoiceSection/QuotesSection.
  const { isLoading: isLoadingInvoiceSettings } = useInvoiceSettings();
  const { isLoading: isLoadingQuoteSettings } = useQuoteSettings();
  const { features, entitlements, isLoading: isLoadingFeatures, hasLoaded: featuresLoaded, refreshEntitlements } = useFeatures();
  // Phase 2.5 — cancel-subscription is one of the three Owner-exclusive
  // capabilities (§2.4: cancel_subscription / delete_tenant /
  // transfer_ownership). Backend enforces via requireOwnerRole on
  // POST /:companyId/subscription/cancel (entitlementRoutes.js); this is
  // the matching FE convenience-hide so non-Owner Admins never see the
  // "Avsluta plan" button.
  const { capabilities: caps } = useCapabilities();

  // Helper function to check features
  const hasFeature = (featureName) => {
    return features && features[featureName] === true;
  };

  // Sections the current tenant may see (gated ones filtered out), the
  // active section resolved from the :section URL param, and the slug we
  // actually render (falls back to the default before the redirect lands).
  const availableSections = SETTINGS_SECTIONS.filter(
    (s) => !s.feature || hasFeature(s.feature)
  );
  const activeSection = availableSections.find((s) => s.slug === section) || null;
  const activeSlug = activeSection?.slug || DEFAULT_SECTION;

  // State for company details
  const [companyDetails, setCompanyDetails] = useState({
    companyName: "",
    companyType: "",
    accountingRegime: "",
    registrationNumber: "",
    vatNumber: "",
    // Session 3 — momsstatus per ML 10 / 18 kap.
    vatStatus: "registered",
    vatExemptionReason: "",
    // Momsprofil — decides which momsdeklaration deadline rule applies
    // (R-DL-2/4/5). All three default to false. That is harmless for
    // vat_large_company and byråanstånd, but NOT for EU trade: R-DL-4 pulls a
    // helår deadline EARLIER, so a tenant that trades within the EU and has
    // not ticked the box is shown a date that is too late. The backend
    // attaches an explicit notice to those periods.
    euTrade: false,
    vatLargeCompany: false,
    byraanstand: false,
    psQuarterlyApproved: false,
    psFilingChannel: null,
    companyEmail: "",
    companyPhone: "",
    contactEmail: "",
    contactPhone: "",
    contactPerson: "",
    address: "",
    city: "",
    postalCode: "",
    country: ""
  });

  const [fiscalYears, setFiscalYears] = useState([]);
  const [files, setFiles] = useState([]);
  const [templates, setTemplates] = useState([]);
  // Tab default is set lazily below — once templates load, jump to
  // whichever tab actually has content. Until then start on 'private'
  // (the historical default). Without this, a tenant with no private
  // templates lands on an empty tab and thinks the system is broken
  // even when 17 shared templates are available one click away.
  const [activeTemplateTab, setActiveTemplateTab] = useState('private');
  const [templateTabAutoSelected, setTemplateTabAutoSelected] = useState(false);
  const [associatedUsers, setAssociatedUsers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [allRoles, setAllRoles] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [userEmployeeMappings, setUserEmployeeMappings] = useState([]);
  const [mappingSuggestions, setMappingSuggestions] = useState([]);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkTargetUser, setLinkTargetUser] = useState(null);
  const [accountPlan, setAccountPlan] = useState([]);

  // New state for SIE verification
  const [showSieVerificationModal, setShowSieVerificationModal] = useState(false);
  const [sieVerificationData, setSieVerificationData] = useState(null);
  const [sieImportResult, setSieImportResult] = useState(null);
  const [sieImportError, setSieImportError] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  const [showSieExportModal, setShowSieExportModal] = useState(false);

  const [showCreateFiscalYearModal, setShowCreateFiscalYearModal] = useState(false);

  // Subscription tab state
  const [plans, setPlans] = useState([]);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [resourceUsage, setResourceUsage] = useState(null);
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [selectedPlanOffer, setSelectedPlanOffer] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Agent settings state
  const [agentSettings, setAgentSettings] = useState({
    approverEmail: '',
    autoApproveMaxAmountSek: 5000,
    autoApproveMinConfidence: 0.9,
    autoApproveKnownSupplierOnly: true,
    approvalChannel: 'email',
    slack: null,
    teams: null,
  });
  const [agentSettingsLoading, setAgentSettingsLoading] = useState(false);
  const [agentSettingsSaving, setAgentSettingsSaving] = useState(false);

  // Track which tabs have loaded their data
  // Fetched-once guard per resource. A ref, not state: it is read and written
  // inside the same handler call, so a state update would land too late and
  // two sections needing the same resource would both fetch it.
  const loadedResources = useRef(new Set());
  const [usersDataLoaded, setUsersDataLoaded] = useState(false);
  const [subscriptionDataLoaded, setSubscriptionDataLoaded] = useState(false);
  const [agentDataLoaded, setAgentDataLoaded] = useState(false);
  const fetchSubscriptionData = useCallback(async () => {
    try {
      const [plansRes, historyRes, usageRes, stripeRes] = await Promise.all([
        axios.get(getApiUrl('plans'), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/subscription/history`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/subscription/resource-usage`), { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(getApiUrl('subscription/stripe/config'), { withCredentials: true }).catch(() => ({ data: { configured: false } }))
      ]);
      setPlans(plansRes.data || []);
      setSubscriptionHistory(historyRes.data || []);
      setResourceUsage(usageRes.data);
      setStripeConfigured(stripeRes.data?.configured || false);
      setSubscriptionDataLoaded(true);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    }
  }, [companyId]);

  const fetchAgentSettings = useCallback(async () => {
    try {
      setAgentSettingsLoading(true);
      const res = await axios.get(
        getApiUrl(`${companyId}/settings/invoice_agent_settings`),
        { withCredentials: true }
      );
      if (res.data?.value) {
        const val = typeof res.data.value === 'string'
          ? JSON.parse(res.data.value)
          : res.data.value;
        setAgentSettings(prev => ({ ...prev, ...val }));
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching agent settings:', error);
      }
    } finally {
      setAgentSettingsLoading(false);
      setAgentDataLoaded(true);
    }
  }, [companyId]);

  const saveAgentSettings = async () => {
    try {
      setAgentSettingsSaving(true);
      try {
        await axios.put(
          getApiUrl(`${companyId}/settings/invoice_agent_settings`),
          { value: agentSettings },
          { withCredentials: true }
        );
      } catch (err) {
        if (err.response?.status === 404) {
          await axios.post(
            getApiUrl(`${companyId}/settings`),
            { key: 'invoice_agent_settings', value: agentSettings },
            { withCredentials: true }
          );
        } else {
          throw err;
        }
      }
      toast.success('Agentinställningar sparade');
    } catch (error) {
      console.error('Error saving agent settings:', error);
      toast.error('Kunde inte spara agentinställningar');
    } finally {
      setAgentSettingsSaving(false);
    }
  };

  // Handle addon checkout via Stripe
  const handleCancelSubscription = async () => {
    const confirmed = await swal({
      title: 'Avsluta prenumeration?',
      text: 'Din plan fortsätter gälla till slutet av nuvarande faktureringsperiod.',
      icon: 'warning',
      buttons: ['Avbryt', 'Ja, avsluta'],
      dangerMode: true,
    });
    if (!confirmed) return;

    try {
      setCancelLoading(true);
      await axios.post(
        getApiUrl(`${companyId}/subscription/cancel`),
        {},
        { withCredentials: true }
      );
      toast.success('Prenumerationen avslutas vid periodens slut.');
      refreshEntitlements();
      fetchSubscriptionData();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      const message = error.response?.data?.error || 'Kunde inte avsluta prenumerationen';
      toast.error(message);
    } finally {
      setCancelLoading(false);
    }
  };

  // Handle return from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment === 'success' || payment === 'plan_success' || payment === 'method_success') {
      const sessionId = params.get('session_id');
      if (sessionId) {
        axios.post(getApiUrl(`${companyId}/subscription/checkout/confirm`), { sessionId })
          .then(({ data }) => {
            toast.info(data.pending ? 'Betalningen behandlas fortfarande.' : 'Uppgifterna från Stripe har bekräftats.');
            refreshEntitlements();
            fetchSubscriptionData();
          }).catch(() => toast.info('Bekräftelsen inväntas. Kontrollera betalningsstatus under Prenumeration.'));
      }
    } else if (payment === 'cancelled') {
      toast.info('Betalningen avbröts.');
    }
    if (payment) {
      params.delete('payment');
      params.delete('session_id');
      const cleanUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Which sections need which resource. Keyed per resource rather than per
  // section since the Bokföring split: five routes share three fetches, and
  // visiting Räkenskapsår should no longer also pull 95 templates and 1 503
  // accounts the way the one combined page did.
  const SECTION_RESOURCES = {
    'fiscal-years': ['fiscalYears'],
    // Hälsa needs a fiscal year id; Konton needs the plan.
    chart: ['accountPlan', 'fiscalYears'],
    templates: ['templates'],
    // Avdragsprofilen is per räkenskapsår.
    vat: ['fiscalYears'],
    'import-export': [],
  };

  const handleTabSelect = (key) => {
    const resources = SECTION_RESOURCES[key];
    if (resources) {
      const pending = [];
      if (resources.includes('fiscalYears') && !loadedResources.current.has('fiscalYears')) {
        loadedResources.current.add('fiscalYears');
        pending.push(fetchFiscalYears());
      }
      if (resources.includes('templates') && !loadedResources.current.has('templates')) {
        loadedResources.current.add('templates');
        pending.push(fetchTemplates());
      }
      if (resources.includes('accountPlan') && !loadedResources.current.has('accountPlan')) {
        loadedResources.current.add('accountPlan');
        pending.push(fetchAccountPlan());
      }
      if (pending.length) Promise.all(pending);
    }
    if (key === 'users' && !usersDataLoaded) {
      setUsersDataLoaded(true);
      Promise.all([fetchAssociatedUsers(), fetchPendingInvitations(), fetchAllRoles(), fetchUserEmployeeMappings(), fetchMappingSuggestions()]);
    }
    if (key === 'subscription' && !subscriptionDataLoaded) {
      fetchSubscriptionData();
    }
    if (key === 'agents' && !agentDataLoaded) {
      fetchAgentSettings();
    }
  };

  const navigateToFiscalYearDetails = (fiscalYearId) => () => {
    // Absolute path — the old relative push resolved against the current
    // URL and broke when the settings section slug moved into the path
    // (/settings/bookkeeping + "accounting/…" → /settings/accounting/…,
    // which matches no route).
    history.push(`/${companyId}/accounting/fiscal-years/${fiscalYearId}`);
  };

  const navigateToTemplateEditor = (templateId = 'new') => {
    history.push(`/${companyId}/templates/${templateId}`);
  };

  // Submit private template for sharing. Routes through the new
  // bookkeeping-templates service (template-system-redesign).
  const handleSubmitForSharing = async (templateId) => {
    try {
      const result = await swal({
        title: "Dela Mall",
        text: "Vill du dela denna mall med alla företag? Den kommer att granskas av en administratör.",
        icon: "info",
        buttons: {
          cancel: "Avbryt",
          confirm: "Dela Mall"
        },
      });

      if (result) {
        const response = await submitTemplateForSharing(companyId, templateId);
        if (response.success) {
          toast.success("Mall skickad för granskning!");
          await fetchTemplates();
        }
      }
    } catch (error) {
      console.error('Error submitting template for sharing:', error);
      toast.error(`Kunde inte dela mallen: ${error.response?.data?.message || 'Okänt fel'}`);
    }
  };

  // Delete (soft-delete) a private template. Confirms first — it
  // removes the mall from the picker; only tenant-owned templates can
  // be deleted (the backend 404s system/shared ones).
  const handleDeleteTemplate = async (templateId, templateName) => {
    try {
      const confirmed = await swal({
        title: 'Ta bort mall',
        text: `Vill du ta bort mallen "${templateName}"? Den försvinner från mallistan.`,
        icon: 'warning',
        buttons: {
          cancel: 'Avbryt',
          confirm: 'Ta bort',
        },
        dangerMode: true,
      });
      if (confirmed) {
        await deleteBookkeepingTemplate(companyId, templateId);
        toast.success('Mallen togs bort.');
        await fetchTemplates();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(`Kunde inte ta bort mallen: ${error.response?.data?.message || 'Okänt fel'}`);
    }
  };

  // Fetch all settings on component mount
  useEffect(() => {
    fetchAllSettings();
  }, [companyId]);

  // Canonicalize the URL: a bare /settings, an unknown slug, or a slug the
  // tenant isn't entitled to all redirect to the default section. Waits for
  // features (BOTH flags: isLoading only covers an in-flight fetch — before
  // the first fetch starts it is false while features are still the all-off
  // defaults, which bounced direct links to gated sections) so gated slugs
  // aren't redirected before hasFeature is reliable.
  useEffect(() => {
    if (isLoadingFeatures || !featuresLoaded) return;
    if (activeSection) return;

    // A retired slug goes where its content went, keyed on the old anchor.
    const legacy = LEGACY_SECTION_REDIRECTS[section];
    if (legacy) {
      const anchor = decodeURIComponent((hash || '').replace(/^#/, ''));
      const target = legacy.byHash[anchor] || legacy.default;
      history.replace(`/${companyId}/settings/${target}`);
      return;
    }

    history.replace(`/${companyId}/settings/${DEFAULT_SECTION}`);
  }, [section, hash, isLoadingFeatures, featuresLoaded, activeSection, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-section lazy data load. Drives the same handleTabSelect used by the
  // old <Tabs onSelect>, now keyed on the active slug — the *DataLoaded
  // guards inside still ensure each section fetches at most once per mount.
  useEffect(() => {
    handleTabSelect(activeSlug);
  }, [activeSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link within a section: scroll to the #anchor card once the active
  // section (and its lazy-loaded data) has rendered.
  useHashScroll([activeSlug]);

  const fetchAllSettings = async () => {
    setIsLoading(true);
    try {
      // Only fetch company details for the default tab.
      // Other tabs lazy-load their data on first select.
      await fetchCompanyDetails();
      setIsLoading(false);
    } catch (error) {
      toast.error("Failed to load settings");
      setIsLoading(false);
    }
  };

  const fetchCompanyDetails = async () => {
    try {
      const url = getApiUrl(`${companyId}/companydetails`);
      const { data } = await axios.get(url, { withCredentials: true });

      setCompanyDetails({
        companyName: data.company_name || "",
        companyType: data.company_type || "",
        accountingRegime: data.accounting_regime || "",
        registrationNumber: data.registration_number || "",
        vatNumber: data.vat_number || "",
        vatStatus: data.vat_status || "registered",
        vatExemptionReason: data.vat_exemption_reason || "",
        euTrade: Boolean(data.eu_trade),
        vatLargeCompany: Boolean(data.vat_large_company),
        byraanstand: Boolean(data.byraanstand),
        psQuarterlyApproved: Boolean(data.psQuarterlyApproved),
        psFilingChannel: data.psFilingChannel || null,
        companyEmail: data.company_email || "",
        companyPhone: data.company_phone || "",
        contactEmail: data.contact_email || "",
        contactPhone: data.contact_phone || "",
        contactPerson: data.contact_person || "",
        address: data.address || "",
        city: data.city || "",
        postalCode: data.postal_code || "",
        country: data.country || "",
        // Read-only TIC enrichment facts (shown in CompanyDetailsSection,
        // refreshed via the "Hämta uppgifter" button)
        ticCompanyId: data.tic_company_id || null,
        companyRegistrationDate: data.company_registration_date || null,
        sniCode: data.sni_code || "",
        sniDescription: data.sni_description || "",
        fTaxRegistered: data.f_tax_registered ?? null,
        payrollRegistered: data.payroll_registered ?? null,
        homepage: data.homepage || "",
        employeeCount: data.employee_count ?? null,
        businessPurpose: data.business_purpose || "",
        registeredOffice: data.registered_office || "",
        ticEnrichedAt: data.tic_enriched_at || null
      });
    } catch (error) {
      console.error('Error getting company details:', error);
      throw error;
    }
  };

  const fetchFiscalYears = async () => {
    try {
      const url = getApiUrl(`${companyId}/accounting/fiscalyears`);
      const { data } = await axios.get(url, { withCredentials: true });
      setFiscalYears(data.fiscalYears);
    } catch (error) {
      console.error('Error getting fiscal years:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      // Routes through the new bookkeeping-templates service. The
      // returned rows carry the same `template_id`, `template_name`,
      // `description`, `approval_status`, `source` fields the existing
      // settings UI already consumes — plus `body` and
      // `source_citations` which the UI ignores for now.
      const rows = await listBookkeepingTemplates(companyId);
      setTemplates(rows);

      // First-load convenience: if the user has no private templates
      // but DOES have shared/pending ones, jump them to the 'shared'
      // tab automatically so they see the catalog content. Only fires
      // once per page load (templateTabAutoSelected guard) so manual
      // tab switches stick afterwards.
      if (!templateTabAutoSelected) {
        const privateCount = rows.filter((t) => t.source === 'tenant').length;
        const sharedCount = rows.filter((t) => t.source === 'shared' || t.source === 'pending').length;
        if (privateCount === 0 && sharedCount > 0) {
          setActiveTemplateTab('shared');
        }
        setTemplateTabAutoSelected(true);
      }
    } catch (error) {
      console.error('Error getting templates:', error);
    }
  };

  // Phase 1.2 (access-control redesign) note: the fetches below load the
  // TENANT's user list, the role catalog, and pending invitations — all
  // multi-user / multi-row data that CapabilityContext does NOT subsume.
  // For caller-SELF checks ("am I admin? what scope can I see?") use
  // useCapabilities() from src/context/CapabilityContext.js. The ad-hoc
  // role data fetched here is only consumed by the admin-list UI
  // (associatedUsers, allRoles, pendingInvitations).
  const fetchAssociatedUsers = async () => {
    try {
      const url = getApiUrl(`${companyId}/users`);
      const { data } = await axios.get(url, { withCredentials: true });
      setAssociatedUsers(data.users);
    } catch (error) {
      console.error('Error getting associated users:', error);
    }
  };

  const fetchPendingInvitations = async () => {
    try {
      const url = getApiUrl(`${companyId}/invitations`);
      const { data } = await axios.get(url, { withCredentials: true });
      setPendingInvitations(data.invitations);
    } catch (error) {
      console.error('Error getting invitations:', error);
    }
  };

  const fetchAllRoles = async () => {
    try {
      const url = getApiUrl(`${companyId}/roles`);
      const { data } = await axios.get(url, { withCredentials: true });
      setAllRoles(data.roles);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchUserEmployeeMappings = async () => {
    try {
      const url = getApiUrl(`${companyId}/hr/user-employee-mappings`);
      const { data } = await axios.get(url, { withCredentials: true });
      setUserEmployeeMappings(data.data || []);
    } catch (error) {
      console.error('Error fetching user-employee mappings:', error);
    }
  };

  const fetchMappingSuggestions = async () => {
    try {
      const url = getApiUrl(`${companyId}/hr/user-employee-mappings/suggestions`);
      const { data } = await axios.get(url, { withCredentials: true });
      setMappingSuggestions(data.data || []);
    } catch (error) {
      console.error('Error fetching mapping suggestions:', error);
    }
  };

  const getMappingForUser = (userId) => {
    return userEmployeeMappings.find(m => m.global_user_id === userId);
  };

  const handleOpenLinkModal = (user) => {
    setLinkTargetUser(user);
    setShowLinkModal(true);
  };

  const handleUnlinkEmployee = async (userId) => {
    try {
      const result = await swal({
        title: "Koppla bort anställd?",
        text: "Kopplingen mellan användaren och den anställde tas bort",
        icon: "warning",
        buttons: ["Avbryt", "Koppla bort"],
        dangerMode: true,
      });

      if (result) {
        const url = getApiUrl(`${companyId}/hr/user-employee-mappings/${userId}`);
        await axios.delete(url, { withCredentials: true });
        toast.success("Koppling borttagen");
        fetchUserEmployeeMappings();
        fetchMappingSuggestions();
      }
    } catch (error) {
      toast.error("Kunde inte ta bort kopplingen");
    }
  };

  const handleApplySuggestion = async (suggestion) => {
    try {
      const url = getApiUrl(`${companyId}/hr/user-employee-mappings`);
      await axios.post(url, {
        globalUserId: suggestion.user_id,
        employeeId: suggestion.employee_id
      }, { withCredentials: true });
      toast.success(`${suggestion.user_name} kopplad till ${suggestion.employee_name}`);
      fetchUserEmployeeMappings();
      fetchMappingSuggestions();
    } catch (error) {
      toast.error(error.response?.data?.message || "Kunde inte skapa koppling");
    }
  };

  const handleEmployeeLinked = () => {
    fetchUserEmployeeMappings();
    fetchMappingSuggestions();
  };

  const fetchAccountPlan = async () => {
    try {
      const url = getApiUrl(`${companyId}/accounting/accounts`);
      const { data } = await axios.get(url, { withCredentials: true });
      setAccountPlan(data.accounts);
    } catch (error) {
      console.error('Error getting account plan:', error);
    }
  };

  const handleFiscalYearAction = async (fiscalYearId, action) => {
    const actionApiUrl = getApiUrl(`${companyId}/accounting/fiscalyears/${fiscalYearId}/${action}`);
    try {
      const { data } = await axios.put(actionApiUrl, {}, { withCredentials: true });
      if (data.success) {
        toast.success(data.message);
        fetchFiscalYears();
      }
    } catch (error) {
      toast.error(`Failed to ${action} fiscal year`);
    }
  };

  // const handleUpload = async () => {
  //   if (files.length === 0) {
  //     swal("Error", "Please select a file to upload", "error");
  //     return;
  //   }
  //   setIsLoading(true);

  //   const formData = new FormData();
  //   formData.append('file', files[0]);

  //   try {
  //     const url = getApiUrl(`${companyId}/verify-se-file`);
  //     const { data } = await axios.post(url, formData, {
  //       withCredentials: true,
  //       headers: { 'Content-Type': 'multipart/form-data' },
  //     });

  //     console.log('Verification response:', data);

  //     if (data.success) {
  //       // Ensure the verification data has the expected structure
  //       const structuredData = {
  //         ...data,
  //         verification: data.verification || {
  //           validation: {
  //             isValid: false,
  //             errors: [],
  //             warnings: [],
  //             summary: {
  //               totalErrors: 0,
  //               totalWarnings: 0,
  //               verificationsChecked: 0,
  //               accountsChecked: 0
  //             }
  //           },
  //           preview: data.preview || {}
  //         }
  //       };

  //       // Show verification modal
  //       setSieVerificationData(structuredData);
  //       setShowSieVerificationModal(true);
  //     } else {
  //       swal("Error", data.message || "Failed to verify the file", "error");
  //     }
  //   } catch (error) {
  //     console.error('Verification error:', error);
  //     swal("Error", "Failed to verify the file", "error");
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  // // Function to handle confirmed import
  // const handleConfirmImport = async () => {
  //   setIsImporting(true);

  //   const formData = new FormData();
  //   formData.append('file', files[0]);
  //   formData.append('fileId', sieVerificationData.fileId);
  //   formData.append('importOptions', JSON.stringify({}));

  //   try {
  //     const url = getApiUrl(`${companyId}/import-se-file-confirmed`);
  //     const { data } = await axios.post(url, formData, {
  //       withCredentials: true,
  //       headers: { 'Content-Type': 'multipart/form-data' },
  //     });

  //     if (data.success) {
  //       swal("Success", "SIE-filen har importerats framgångsrikt!", "success");
  //       setShowSieVerificationModal(false);
  //       setSieVerificationData(null);
  //       setFiles([]);
  //       fetchFiscalYears();
  //     } else {
  //       swal("Error", data.message || "Failed to import the file", "error");
  //     }
  //   } catch (error) {
  //     console.error('Import error:', error);
  //     swal("Error", "Failed to import the file", "error");
  //   } finally {
  //     setIsImporting(false);
  //   }
  // };

  // Add these functions to handle the enhanced workflow:

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Välj en SIE-fil att kontrollera först');
      return;
    }
    setIsLoading(true);
    setSieImportResult(null);
    setSieImportError(null);

    const formData = new FormData();
    formData.append('file', files[0]);

    try {
      const url = getApiUrl(`${companyId}/accounting/sie/verify`);
      const { data } = await axios.post(url, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (data.success) {
        // Store the file data for later import
        const structuredData = {
          ...data,
          fileId: data.fileId,
          sessionId: data.sessionId,
          accountReconciliation: data.accountReconciliation,
          originalFile: files[0], // Keep reference to original file
          verification: data.validation,
          preview: data.preview
        };

        setSieVerificationData(structuredData);
        setShowSieVerificationModal(true);
      } else {
        toast.error(data.message || 'Filen kunde inte kontrolleras');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Filen kunde inte kontrolleras. Försök igen.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmImport = async (fiscalYearSettings, { acceptErrors = false } = {}) => {
    setIsImporting(true);
    setSieImportError(null);

    const formData = new FormData();
    formData.append('file', sieVerificationData.originalFile);
    formData.append('fileId', sieVerificationData.fileId);
    // Binds the import to the reviewed session. The backend re-hashes the file
    // and refuses a different one, a stale chart, or an outstanding decision.
    formData.append('sessionId', sieVerificationData.sessionId || sieVerificationData.fileId);
    // The backend gate refuses error-carrying files without this explicit
    // acknowledgement ("importera trots fel").
    formData.append('acceptErrors', acceptErrors ? 'true' : 'false');

    const importOptions = {
      fiscalYearSettings: fiscalYearSettings
    };
    formData.append('importOptions', JSON.stringify(importOptions));

    try {
      const url = getApiUrl(`${companyId}/accounting/sie/import`);
      const { data } = await axios.post(url, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (data.success) {
        // Keep the modal open and show the import result (counts) instead
        // of closing on a toast the user may miss.
        setSieImportResult(data.data || {});
        setFiles([]);

        // Refresh bookkeeping data after import
        await Promise.all([fetchFiscalYears(), fetchAccountPlan()]);
      } else {
        setSieImportError(data.message || 'Importen misslyckades');
      }
    } catch (error) {
      console.error('Import error:', error);
      // Gate refusals (fel företag, redan importerad, fel utanför året …)
      // come back as 422 with a Swedish message — show it in the modal
      // where the user is, not as a vanishing toast.
      setSieImportError(error.response?.data?.message
        || 'Importen misslyckades av ett tekniskt fel. Ingen data har importerats.');
    } finally {
      setIsImporting(false);
    }
  };

  // Function to handle cancel
  const handleCancelImport = () => {
    setShowSieVerificationModal(false);
    setSieVerificationData(null);
    setSieImportResult(null);
    setSieImportError(null);
    setFiles([]);
  };

  const handleOpenExportModal = () => {
    setShowSieExportModal(true);
  };

  const handleCloseExportModal = () => {
    setShowSieExportModal(false);
  };

  const handleCloseModal = () => {
    setShowCreateFiscalYearModal(false);
  };

  const handleSaveFiscalYear = async (fiscalYearData) => {
    try {
      const url = getApiUrl(`${companyId}/accounting/fiscalyears`);
      const { data } = await axios.post(url, fiscalYearData, { withCredentials: true });

      if (data.success) {
        toast.success("Räkenskapsår skapades framgångsrikt");
        setShowCreateFiscalYearModal(false);
        // Refresh the fiscal years list
        await fetchFiscalYears();
      } else {
        toast.error(data.message || "Kunde inte skapa räkenskapsår");
      }
    } catch (error) {
      console.error('Error creating fiscal year:', error);
      const errorMessage = error.response?.data?.message || "Kunde inte skapa räkenskapsår";
      toast.error(errorMessage);
    }
  };

  const saveCompanyDetails = async () => {
    try {
      const url = getApiUrl(`${companyId}/companydetails`);
      // Map frontend field names to backend expected field names
      const payload = {
        // Backend service expects 'company_name' (snake_case) for company name updates
        company_name: companyDetails.companyName,
        companyType: companyDetails.companyType,
        registrationNumber: companyDetails.registrationNumber,
        vatNumber: companyDetails.vatNumber,
        vatStatus: companyDetails.vatStatus,
        vatExemptionReason: companyDetails.vatStatus === 'exempt'
          ? (companyDetails.vatExemptionReason || '').trim()
          : null,
        euTrade: Boolean(companyDetails.euTrade),
        vatLargeCompany: Boolean(companyDetails.vatLargeCompany),
        byraanstand: Boolean(companyDetails.byraanstand),
        psQuarterlyApproved: Boolean(companyDetails.psQuarterlyApproved),
        psFilingChannel: companyDetails.psFilingChannel || null,
        address: companyDetails.address,
        city: companyDetails.city,
        country: companyDetails.country,
        postalCode: companyDetails.postalCode ? companyDetails.postalCode.replace(/\s/g, '') : '',
        contactEmail: companyDetails.contactEmail,
        contactPhone: companyDetails.contactPhone,
        contactPerson: companyDetails.contactPerson,
        companyEmail: companyDetails.companyEmail,
        companyPhone: companyDetails.companyPhone
      };
      await axios.put(url, payload, { withCredentials: true });
      toast.success("Företagsuppgifterna har sparats");
    } catch (error) {
      // Surface server-side validation messages (ML 17 kap reason-required etc.)
      const serverMsg = error?.response?.data?.message || error?.response?.data?.error;
      toast.error(serverMsg || "Det gick inte att spara företagsuppgifterna");
    }
  };

  const saveLegacyK2Correction = async () => {
    try {
      const url = getApiUrl(`${companyId}/companydetails/accounting-regime`);
      const { data } = await axios.put(
        url,
        { accountingRegime: 'k2', legacyCorrectionConfirmed: true },
        { withCredentials: true }
      );
      toast.success(`Det befintliga K2-regelverket har registrerats (${data.accountingRegime.toUpperCase()}).`);
      await fetchCompanyDetails();
    } catch (error) {
      const serverMsg = error?.response?.data?.message || error?.response?.data?.error;
      toast.error(serverMsg || 'Det gick inte att spara redovisningsregelverket.');
    }
  };

  const handleOpenRoleModal = (user) => {
    setSelectedUser(user);
    setShowRoleModal(true);
  };

  const handleCloseRoleModal = () => {
    setShowRoleModal(false);
    setSelectedUser(null);
  };

  const handleSaveRoles = async (userId, roleNames) => {
    // Backend `RoleController.updateUserRoles` (PUT /:companyId/users/:userId/roles)
    // reads `req.body.roles` as an array of role NAMES — see
    // accounting-backend ACCESS_CONTROL_REDESIGN §0.4. The variable was
    // historically misnamed `roleIds` and shipped under the same body key,
    // which made the controller's `const { roles }` destructure return
    // undefined and 400 on every call. Both the variable name and the
    // payload key now match what the backend expects.
    try {
      const url = getApiUrl(`${companyId}/users/${userId}/roles`);
      await axios.put(url, { roles: roleNames }, { withCredentials: true });
      toast.success("User roles updated successfully");
      fetchAssociatedUsers();
      handleCloseRoleModal();
    } catch (error) {
      // Backend ConflictError (Owner singleton-with-transfer) deserves a more
      // specific toast than the generic "Failed" — surface the server message
      // when present so the user understands they need transfer-ownership.
      const serverMsg = error?.response?.data?.error || error?.response?.data?.message;
      toast.error(serverMsg || "Failed to update user roles");
    }
  };

  const canRemoveUser = (userId) => {
    // Find the user being removed
    const userToRemove = associatedUsers.find(user => user.user_id === userId);

    if (userToRemove?.roles?.some(role => role.toLowerCase() === 'owner')) {
      return { canRemove: false, reason: 'Överlåt ägarskapet innan ägaren lämnar företaget.' };
    }
    if (userId === caps?.userId) {
      return { canRemove: false, reason: 'Använd Lämna i dina användarinställningar för att lämna företaget.' };
    }

    return { canRemove: true };
  };

  const handleRemoveUser = async (userId) => {
    try {
      // Check if user can be removed
      const { canRemove, reason } = canRemoveUser(userId);

      if (!canRemove) {
        toast.warning(reason);
        return;
      }

      const result = await swal({
        title: "Är du säker?",
        text: "Detta kommer att ta bort användaren från företaget",
        icon: "warning",
        buttons: ["Avbryt", "Ta bort"],
        dangerMode: true,
      });

      if (result) {
        const url = getApiUrl(`${companyId}/users/${userId}`);
        await axios.delete(url, { withCredentials: true });
        toast.success("Användaren har tagits bort");
        fetchAssociatedUsers();
      }
    } catch (error) {
      toast.error("Kunde inte ta bort användaren");
    }
  };

  const handleInviteUser = async (email, roles) => {
    try {

      const url = getApiUrl(`${companyId}/invitations`);
      const response = await axios.post(url, { email, roles }, { withCredentials: true });
      if (response.data.emailDelivery === 'failed') toast.warning('Inbjudan sparades men mejlet kunde inte skickas. Använd Skicka igen.');
      else toast.success('Inbjudan har skickats');
      fetchPendingInvitations();
      setShowInviteModal(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Kunde inte skicka inbjudan");
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      const url = getApiUrl(`${companyId}/invitations/${invitationId}/resend`);
      const response = await axios.post(url, {}, { withCredentials: true });
      if (response.data.emailDelivery === 'failed') toast.warning('Mejlet kunde inte skickas. Inbjudan finns kvar så att du kan försöka igen.');
      else toast.success('Inbjudan har skickats igen');
      fetchPendingInvitations();
    } catch (error) {
      toast.error("Failed to resend invitation");
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    try {
      const result = await swal({
        title: "Are you sure?",
        text: "This will delete the invitation",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (result) {
        const url = getApiUrl(`${companyId}/invitations/${invitationId}`);
        await axios.delete(url, { withCredentials: true });
        toast.success("Invitation deleted successfully");
        fetchPendingInvitations();
      }
    } catch (error) {
      toast.error("Failed to delete invitation");
    }
  };

  if (isLoading || isLoadingInvoiceSettings || isLoadingQuoteSettings || !featuresLoaded) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        icon={SettingsIcon}
        title="Inställningar"
        subtitle="Företagsuppgifter, bokföring, integrationer och automatisering."
      />

      <div className="settings-layout">
        <SettingsRail
          basePath={`/${companyId}/settings`}
          activeSlug={activeSlug}
          sections={availableSections}
          defaultCollapsedGroups={DEFAULT_COLLAPSED_GROUPS}
        />

        <div className="settings-content">
          {activeSlug === 'company' && (
            <CompanyDetailsSection
              companyDetails={companyDetails}
              setCompanyDetails={setCompanyDetails}
              saveCompanyDetails={saveCompanyDetails}
              saveLegacyK2Correction={saveLegacyK2Correction}
              canCorrectLegacyAccountingRegime={Boolean(caps.isOwner)}
              companyId={companyId}
              onEnriched={fetchCompanyDetails}
            />
          )}

          {activeSlug === 'fiscal-years' && (
            <FiscalYearsSection
              fiscalYears={fiscalYears}
              setShowCreateFiscalYearModal={setShowCreateFiscalYearModal}
              navigateToFiscalYearDetails={navigateToFiscalYearDetails}
              handleFiscalYearAction={handleFiscalYearAction}
            />
          )}

          {activeSlug === 'chart' && (
            <ChartSection
              companyId={companyId}
              companyType={companyDetails.companyType}
              fiscalYears={fiscalYears}
            />
          )}

          {activeSlug === 'templates' && (
            <TemplatesSection
              templates={templates}
              navigateToTemplateEditor={navigateToTemplateEditor}
              handleSubmitForSharing={handleSubmitForSharing}
              handleDeleteTemplate={handleDeleteTemplate}
            />
          )}

          {activeSlug === 'vat' && (
            <VatSection
              companyId={companyId}
              companyDetails={companyDetails}
              setCompanyDetails={setCompanyDetails}
              saveCompanyDetails={saveCompanyDetails}
              fiscalYears={fiscalYears}
            />
          )}

          {activeSlug === 'import-export' && (
            <ImportExportSection
              files={files}
              setFiles={setFiles}
              isLoading={isLoading}
              handleUpload={handleUpload}
              handleOpenExportModal={handleOpenExportModal}
            />
          )}

          {activeSlug === 'payroll' && <PayrollSection />}

          {activeSlug === 'automation' && (
            <AutomationSection companyId={companyId} />
          )}

          {activeSlug === 'assets' && (
            <AssetsSection companyId={companyId} />
          )}

          {activeSlug === 'users' && (
            <>
            <OwnershipSection key={companyId} companyId={companyId} userId={caps?.userId} onChanged={fetchAssociatedUsers} />
            <UsersSection
              associatedUsers={associatedUsers}
              pendingInvitations={pendingInvitations}
              mappingSuggestions={mappingSuggestions}
              getMappingForUser={getMappingForUser}
              canRemoveUser={canRemoveUser}
              handleApplySuggestion={handleApplySuggestion}
              handleUnlinkEmployee={handleUnlinkEmployee}
              handleOpenLinkModal={handleOpenLinkModal}
              handleOpenRoleModal={handleOpenRoleModal}
              handleRemoveUser={handleRemoveUser}
              handleResendInvitation={handleResendInvitation}
              handleDeleteInvitation={handleDeleteInvitation}
              setShowInviteModal={setShowInviteModal}
            />
            </>
          )}

          {activeSlug === 'invoice' && <InvoiceSection />}

          {activeSlug === 'quotes' && <QuotesSection />}

          {activeSlug === 'subscription' && (
            <SubscriptionSection
              entitlements={entitlements}
              caps={caps}
              stripeConfigured={stripeConfigured}
              cancelLoading={cancelLoading}
              resourceUsage={resourceUsage}
              plans={plans}
              features={features}
              subscriptionHistory={subscriptionHistory}
              handleCancelSubscription={handleCancelSubscription}
              refreshEntitlements={refreshEntitlements}
              fetchSubscriptionData={fetchSubscriptionData}
              onSelectPlan={(plan, billingCycle) => setSelectedPlanOffer({ companyId, plan, billingCycle })}
            />
          )}

          {activeSlug === 'agents' && (
            <AgentsSection
              agentSettings={agentSettings}
              setAgentSettings={setAgentSettings}
              saveAgentSettings={saveAgentSettings}
              agentSettingsLoading={agentSettingsLoading}
              agentSettingsSaving={agentSettingsSaving}
            />
          )}

          {activeSlug === 'team' && <MeetTheTeamSection />}

          {activeSlug === 'custom-agents' && <CustomAgentsSection />}

          {activeSlug === 'agent-skills' && <AgentSkillsSection />}

          {activeSlug === 'email-inbox' && (
            <EmailInboxSection companyId={companyId} showScanning={hasFeature("email_inbox_scanning")} />
          )}

          {activeSlug === 'bank-connections' && (
            <div id="bankanslutningar">
              <BankConnectionSettings companyId={companyId} />
            </div>
          )}

          {activeSlug === 'zettle' && <ZettleSection companyId={companyId} />}
        </div>
      </div>

      <RoleModal
        show={showRoleModal}
        handleClose={handleCloseRoleModal}
        user={selectedUser}
        allRoles={allRoles}
        handleSaveRoles={handleSaveRoles}
        companyId={companyId}
        canManageRetention={caps?.isOwner === true}
        onRetentionChanged={fetchAssociatedUsers}
      />

      <InviteUserModal
        show={showInviteModal}
        handleClose={() => setShowInviteModal(false)}
        allRoles={allRoles}
        handleInviteUser={handleInviteUser}
      />

      <LinkEmployeeModal
        show={showLinkModal}
        handleClose={() => setShowLinkModal(false)}
        user={linkTargetUser}
        companyId={companyId}
        onLinked={handleEmployeeLinked}
      />

      {sieVerificationData && (
        <SieVerificationModal
          show={showSieVerificationModal}
          onHide={handleCancelImport}
          verificationData={sieVerificationData}
          onConfirmImport={handleConfirmImport}
          onCancel={handleCancelImport}
          isImporting={isImporting}
          importResult={sieImportResult}
          importError={sieImportError}
        />
      )}

      <SieExportModal
        show={showSieExportModal}
        onHide={handleCloseExportModal}
        fiscalYears={fiscalYears}
        companyId={companyId}
      />

      <CreateFiscalYearModal show={showCreateFiscalYearModal} handleClose={handleCloseModal} handleSave={handleSaveFiscalYear} openFiscalYearsCount={fiscalYears.filter(fy => fy.state === 'open').length} />


      {selectedPlanOffer && selectedPlanOffer.companyId === companyId && <PlanUpgradeModal
        key={`${companyId}:${selectedPlanOffer.plan.plan_name}:${selectedPlanOffer.billingCycle}`}
        show
        onHide={() => { setSelectedPlanOffer(null); refreshEntitlements(); fetchSubscriptionData(); }}
        companyId={companyId}
        selectedPlan={selectedPlanOffer.plan}
        billingCycle={selectedPlanOffer.billingCycle}
        hasStripeSubscription={entitlements?.hasStripeSubscription}
        trialEndsAt={entitlements?.trialEndsAt}
      />}

    </>
  );
};

export default CompanySettings;
